import clsx from "clsx";
import { TFunction } from "i18next";
import { useMemo, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ListSearchIcon } from "@/assets/icons/icons";
import { Button } from "@/components/Interface/Button/Button";
import { ListBoxMultiple } from "@/components/Interface/FormFields/ListBoxMultiple/ListBoxMultiple";
import { Pagination } from "@/components/Interface/Pagination/Pagination";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { PageNumberComboBox } from "@/components/Layout/Table/PageNumberComboBox/PageNumberComboBox";
import { TableControlGroup } from "@/components/Layout/Table/TabeControlGroup/TableControlGroup";
import { Table } from "@/components/Layout/Table/Table";
import { useHandleError } from "@/hooks/useHandleError";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useQueryParam } from "@/hooks/useQueryParam";
import {
  getEvnFiles,
  useGetDevices1,
  useGetEvnForCustomer,
} from "@/services/api";
import { DrEvn, EvnFile } from "@/services/model";
import { FCC } from "@/types";
import { log } from "@/utils/log";

import { ItemizedBillOverviewHeader } from "./ItemizedBillOverviewHeader";
import { ItemizedBillOverviewRow } from "./ItemizedBillOverviewRow";

export type ItemizedBillListIdForm = {
  selectedItems: string[]; // stores encoded values
};

type FilterFormDevice = {
  deviceList: string[];
};

type FilterFormPeriod = {
  periods: string[];
};

const DropDownLabel: FCC = ({ children }) => (
  <span className="mb-4 text-lg font-semibold text-secondary-100">
    {children}
  </span>
);

export const assignFileToAnchor = (
  data: EvnFile,
  elem: HTMLAnchorElement,
  t: TFunction<"portal", undefined, "portal">,
): HTMLAnchorElement | undefined => {
  if (data.evnFile?.file && data.evnFile.mimeType === "application/pdf") {
    const filename = `DR_EVN-${new Date()
      .toISOString()
      .replaceAll(/:|\./g, "-")}.pdf`;
    elem.setAttribute("target", "_blank");
    elem.setAttribute("download", filename);

    // On chrome, we cannot open data urls, so we convert to a blob and create it this way
    if (navigator.userAgent.includes("Firefox")) {
      elem.setAttribute(
        "href",
        `data:application/pdf;base64,${data.evnFile?.file}`,
      );
    } else {
      // decode base64 string, remove space for IE compatibility
      const binary = atob(
        (data.evnFile.file as unknown as string).replace(/\s/g, ""),
      );
      const len = binary.length;
      const buffer = new ArrayBuffer(len);
      const view = new Uint8Array(buffer);
      for (let i = 0; i < len; i++) {
        view[i] = binary.charCodeAt(i);
      }

      // create the blob object with content-type "application/pdf"
      const blob = new Blob([view], { type: "application/pdf" });
      elem.setAttribute("href", window.URL.createObjectURL(blob));
    }

    return elem;
  } else if (data.evnFile?.mimeType === "application/pdf") {
    const url = `data:application/zip;base64,${data.evnFile?.file}`;
    const link = document.createElement("a");

    link.download = t("documents.tabItemizedBill.zipName");
    link.href = url;
    link.click();
  } else {
    log("Could not save bill pdf, the check for valid object failed.");
  }
};

export const ItemizedBillOverview = () => {
  const { t } = useTranslation();
  const handleError = useHandleError();
  const isMobile = useIsMobile();

  const [shouldShowEntries, setShouldShowEntries] = useState(false);
  const [isFetchingFile, setIsFetchingFile] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(true);
  const [availablePeriods, setAvailablePeriods] =
    useState<(string | undefined)[]>();
  const [isSelectMultiple, setIsSelectMultiple] = useState(false);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const { watch: watchDevice, control: controlDevice } =
    useForm<FilterFormDevice>({
      defaultValues: {},
    });
  const formStateDevice = watchDevice();

  const { watch: watchPeriod, control: controlPeriod } =
    useForm<FilterFormPeriod>({
      defaultValues: {},
    });
  const formStatePeriod = watchPeriod();
  const hasSelectedDeviceFilter =
    (formStateDevice?.deviceList?.length ?? 0) > 0;
  const hasSelectedPeriodFilter = (formStatePeriod?.periods?.length ?? 0) > 0;

  const { data: itemizedBillDevices, isFetching: isFetchingDevices } =
    useGetDevices1();

  const { data: itemizedBillOverview, isFetching: isFetchingOverview } =
    useGetEvnForCustomer(
      {
        periods: formStatePeriod.periods,
      },
      {
        query: {
          refetchOnWindowFocus: false,
          enabled: shouldFetch,
          onSuccess: (data) => {
            setShouldFetch(false);

            // Initially set available ItemizedBills/EVN so we can utilize the filters correctly
            if (!availablePeriods) {
              setAvailablePeriods([
                ...new Set(data?.evns?.map((evn) => evn.periode)),
              ]);
            }

            // If the total number of pages is smaller than the current selected page,
            // (e.g. we are on page 3 but the data has only 2 pages now)
            // reset to the next possible value
            if (
              data.totalNumberPages &&
              data.totalNumberPages < currentItemizedBillPage
            ) {
              setCurrentItemizedBillPage(data.totalNumberPages.toString());
            }
          },
        },
      },
    );

  const hasItemizedBills = useMemo(
    () => itemizedBillOverview?.evns && itemizedBillOverview?.evns?.length >= 1,
    [itemizedBillOverview?.evns],
  );

  const requestedItemizedBills = useMemo<DrEvn[]>(() => {
    if (shouldShowEntries && !shouldFetch) {
      if (formStateDevice.deviceList && formStateDevice.deviceList.length > 0) {
        return (
          itemizedBillOverview?.evns?.filter((evn) =>
            formStateDevice.deviceList.includes(evn.contract || ""),
          ) || []
        );
      }

      return itemizedBillOverview?.evns ?? [];
    }

    return [];
  }, [
    formStateDevice.deviceList,
    shouldShowEntries,
    shouldFetch,
    itemizedBillOverview?.evns,
  ]);

  const sortedBills = useMemo(() => {
    if (!requestedItemizedBills) return [];

    const sorted = [...requestedItemizedBills];

    sorted.sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (sortField) {
        case "PLAN":
          aVal = a.contract;
          bVal = b.contract;
          break;
        case "PERIOD":
          aVal = a.periode;
          bVal = b.periode;
          break;
        case "AMOUNT":
          aVal = a.amount;
          bVal = b.amount;
          break;
        case "NUMBER":
          aVal = a.billId;
          bVal = b.billId;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;

      return 0;
    });

    return sorted;
  }, [requestedItemizedBills, sortField, sortDirection]);

  const formMethods = useForm<ItemizedBillListIdForm>({
    defaultValues: { selectedItems: [] },
  });

  const selectedValues = formMethods.watch("selectedItems") ?? [];

  const selectedItems = selectedValues.map((value) => {
    const [serviceId, period] = value.split("|");

    return {
      serviceId: Number(serviceId),
      period,
    };
  });

  const downloadItemizedBill = ({
    serviceId,
    period,
  }: {
    serviceId: number;
    period: string;
  }) => {
    setIsFetchingFile(true);

    getEvnFiles({ serviceIds: [serviceId], periods: [period] })
      .then((data) => {
        let elem: HTMLAnchorElement | undefined =
          window.document.createElement("a");
        elem = assignFileToAnchor(data, elem, t);

        if (elem) {
          elem.click();

          // cleanup (important for Safari)
          setTimeout(() => {
            window.URL.revokeObjectURL(elem.href);
            elem.remove();
          }, 100);
        }
      })
      .catch((error) => handleError(error))
      .finally(() => setIsFetchingFile(false));
  };

  const downloadSelectedItemizedBills = () => {
    setIsFetchingFile(true);

    getEvnFiles({
      serviceIds: selectedItems.map((item) => item.serviceId),
      periods: selectedItems.map((item) => item.period),
    })
      .then((data) => {
        if (!data.evnFile?.file || !data.evnFile.mimeType) {
          log("Invalid file response");

          return;
        }

        // Case 1: multiple files -> ZIP
        if (data.evnFile.mimeType === "application/zip") {
          const url = `data:application/zip;base64,${data.evnFile.file}`;
          const link = document.createElement("a");

          link.download = t("documents.tabItemizedBill.zipName");
          link.href = url;
          link.click();

          return;
        }

        // Case 2: single file -> PDF
        if (data.evnFile.mimeType === "application/pdf") {
          const elem = assignFileToAnchor(data, document.createElement("a"), t);

          if (elem) {
            document.body.appendChild(elem);
            elem.click();

            setTimeout(() => {
              window.URL.revokeObjectURL(elem.href);
              elem.remove();
            }, 100);
          }

          return;
        }

        log("Unsupported mime type:", data.evnFile.mimeType);
      })
      .catch((error) => handleError(error))
      .finally(() => setIsFetchingFile(false));
  };

  const [itemizedBillPage, setCurrentItemizedBillPage] = useQueryParam("page");
  const currentItemizedBillPage = Number(itemizedBillPage) || 1;
  const itemizedBillPages = itemizedBillOverview?.totalNumberPages || 1;

  const Info = () => (
    <>
      <ListSearchIcon />
      <p className="my-6 text-xl font-semibold text-primary-100">
        {hasItemizedBills
          ? t("documents.tabItemizedBill.choosePlanAndPeriod")
          : t("documents.tabItemizedBill.noItemizedBillOverview")}
      </p>
      {!shouldShowEntries && hasItemizedBills && (
        <Button
          className="inverted accent text-sm md:w-auto md:text-lg"
          fullWidth={isMobile}
          onClick={() => setShouldShowEntries(true)}
        >
          {t("documents.tabItemizedBill.showAll")}
        </Button>
      )}
    </>
  );

  const MobileView = () => (
    <>
      <div className="flex flex-col">
        <button
          className={clsx(
            "self-end text-base font-semibold text-primary-100 underline",
            isSelectMultiple ? "opacity-50" : "opacity-100",
          )}
          onClick={() => {
            setIsSelectMultiple((prev) => !prev);

            if (!isSelectMultiple) {
              formMethods.resetField("selectedItems");
            }
          }}
        >
          {isSelectMultiple
            ? t("common.buttons.cancel")
            : t("bills.selectMultiple")}
        </button>

        <div className="flex flex-col">
          {sortedBills?.map((itemizedBill) => (
            <ItemizedBillOverviewRow
              key={itemizedBill.id}
              itemizedBill={itemizedBill}
              variant="compact"
              isSelectMultiple={isSelectMultiple}
              downloadItemizedBill={downloadItemizedBill}
            />
          ))}
        </div>
      </div>
      {shouldShowEntries && (
        <TableControlGroup>
          <div className="flex w-full flex-col gap-4">
            <Pagination paginationPages={itemizedBillPages} />
            {isSelectMultiple && (
              <Button
                className="accent"
                disabled={Object.values(selectedValues).length === 0}
                onClick={downloadSelectedItemizedBills}
                fullWidth
              >
                {t("bills.download", { count: selectedValues.length })}
              </Button>
            )}
          </div>
        </TableControlGroup>
      )}
    </>
  );

  const DesktopView = () => (
    <div className="w-full overflow-x-auto">
      <div className="min-w-max">
        <Table paginationPages={itemizedBillPages}>
          <thead>
            <ItemizedBillOverviewHeader
              currentPageItemizedBillIds={
                sortedBills
                  ?.filter(
                    (itemizedBill) =>
                      itemizedBill.serviceId && itemizedBill.periode,
                  )
                  .map((itemizedBill) => ({
                    serviceId: itemizedBill.serviceId!,
                    period: itemizedBill.periode!,
                  })) ?? []
              }
              onSortChange={(field, direction) => {
                setSortField(field);
                setSortDirection(direction);
              }}
            />
          </thead>
          <tbody>
            {sortedBills?.map((itemizedBill) => (
              <ItemizedBillOverviewRow
                key={itemizedBill.id}
                itemizedBill={itemizedBill}
                downloadItemizedBill={downloadItemizedBill}
              />
            ))}
          </tbody>
        </Table>
      </div>
      {shouldShowEntries && (
        <TableControlGroup>
          <div className="mr-8">
            <PageNumberComboBox />
          </div>
          {((isMobile && isSelectMultiple) || !isMobile) && (
            <Button
              className="accent"
              disabled={Object.values(selectedValues).length === 0}
              onClick={downloadSelectedItemizedBills}
            >
              {t("bills.download", { count: selectedValues.length })}
            </Button>
          )}
        </TableControlGroup>
      )}
    </div>
  );

  return (
    <div className="mt-10">
      {(isFetchingDevices || isFetchingOverview || isFetchingFile) && (
        <LoadingSpinner />
      )}
      <p>{t("documents.tabItemizedBill.subtitle")}</p>

      <div className="mt-10 flex w-full flex-col gap-6 md:flex-row">
        <div className="flex w-full flex-col">
          <DropDownLabel>{t("documents.tabItemizedBill.plan")}</DropDownLabel>
          <Controller<FilterFormDevice>
            control={controlDevice}
            name="deviceList"
            render={({ field: { onChange, value } }) => (
              <ListBoxMultiple<string | undefined>
                className="full-width"
                itemLabel={t("Content.rates")}
                placeholder={t(
                  hasSelectedPeriodFilter
                    ? "documents.tabItemizedBill.onlyOneFilterAllowed"
                    : "documents.tabItemizedBill.placeholderPlans",
                )}
                selected={typeof value === "string" ? [value] : value}
                setSelected={onChange}
                displayValue={(value) => value ?? ""}
                availableValues={
                  itemizedBillDevices?.map(({ device }) => device) || []
                }
                disabled={
                  hasSelectedPeriodFilter ||
                  !itemizedBillDevices ||
                  itemizedBillDevices?.length <= 0
                }
                hideArrowWhenDisabled={hasSelectedPeriodFilter}
                showAmountForMultipleSelection
              />
            )}
          />
        </div>

        <div className="flex w-full flex-col">
          <DropDownLabel>{t("documents.tabItemizedBill.period")}</DropDownLabel>
          <Controller<FilterFormPeriod>
            control={controlPeriod}
            name="periods"
            render={({ field: { onChange, value } }) => (
              <ListBoxMultiple<string | undefined>
                className="full-width"
                itemLabel={t("Content.periods")}
                placeholder={t(
                  hasSelectedDeviceFilter
                    ? "documents.tabItemizedBill.onlyOneFilterAllowed"
                    : "documents.tabItemizedBill.placeholderPeriods",
                )}
                selected={typeof value === "string" ? [value] : value}
                setSelected={onChange}
                displayValue={(value) => value ?? ""}
                availableValues={availablePeriods || []}
                disabled={
                  hasSelectedDeviceFilter ||
                  !availablePeriods ||
                  availablePeriods.length <= 0
                }
                hideArrowWhenDisabled={hasSelectedDeviceFilter}
                showAmountForMultipleSelection
              />
            )}
          />
        </div>

        <div className="flex items-end">
          <Button
            fullWidth
            className="accent w-full text-sm md:w-auto md:text-lg"
            onClick={() => {
              setShouldFetch(true);
              setShouldShowEntries(true);
            }}
          >
            {t("documents.tabItemizedBill.btnSearch")}
          </Button>
        </div>
      </div>

      <div className="mt-2 flex flex-col items-center py-10 text-center md:mt-0">
        <FormProvider {...formMethods}>
          {sortedBills?.length > 0 ? (
            isMobile ? (
              <MobileView />
            ) : (
              <DesktopView />
            )
          ) : (
            <Info />
          )}
        </FormProvider>
      </div>
    </div>
  );
};
